// ReferPanel — the "Refer & Save" section host (TD-384 T26, migrations
// 096-099; ⚠️ REWRITTEN 2026-10-04, account-area redesign Task 10 / the
// 2026-10-02 amendment F11/F12/F15/F18-F20/O7).
//
// ORIGINAL: three sub-tabs — Refer a Store (the §4 submission form with a
// merchant-account picker (retired by TD-610) over the caller's ACTIVE MIDs
// + the caller's own referral card list), Redeem Credits (RedeemPanel), Payouts
// (ReferralPayoutsPanel). CURRENT: the four REGISTRY tabs
// (account-sections.ts SECTION_TABS.refer — the single vocabulary; the old
// internal ids and the shell's REFER_TAB_BY_REGISTRY_ID translation map are
// deleted with the pre-rename naming):
//   Welcome     : ReferWelcomePanel.tsx — the sales page (this task; O5)
//   Refer a store : this file — the SHORTEST VALID §4 submission form, the
//                 composed message (textarea + copy), and nothing else
//   Referrals   : this file — the referral history table (moved off the form)
//   Redemptions : RedemptionsPanel.tsx — credit summary + the redeem dialog
//                 (RedeemPanel.tsx) + the merged §30 history
//
// THE FORM: §4's required text fields are the shortest valid set — store
// name, contact first/last/phone/email, city, state (the server's 422 names
// exactly these; it requires phone AND email, so the form does too — the
// "phone or email" wording in the task list was reconciled to the SERVER's
// contract, which is the control). Description and street are optional and
// sit under "More details (optional)". TD-610: a referral names NO merchant
// account (credit is account-level). The server requires a buyer with an
// Aldelo Pay STORE (a live store the caller owns with an active, live MID
// linked to it), so availability of the form follows the redeem-store list's
// `has_active_merchant_account` (same predicate), NOT "any active MID"
// (merchant-type retirement Task 15). A settled read with no such store
// withholds the form with guidance; a FAILED read is UNKNOWN: the form
// renders and the server decides (422 when ineligible).
//
// THE SHARE LINK is offered on the SAME derived value (eligibleOrUnknown): a
// failed store read is UNKNOWN and keeps the link offered.
//
// THE COMPOSED MESSAGE (Task 20 slice 1): an editable textarea prefilled from
// the form fields plus the program tagline (REFER_WELCOME_HEADLINE — the
// Welcome tab's own headline; no config-sourced tagline exists) until the
// merchant edits it manually (a manual edit STOPS the prefill — the
// merchant's words are never overwritten). Under the message: the four share
// actions — Copy message / Email / Text / Share — every one a pure
// CLIENT-SIDE intent (clipboard write, mailto:, sms:, navigator.share).
// S2(i): no message is ever sent by Aldelo — zero network calls on any share
// action. SLICE 2 (migration 147): the share LINK, when the caller has one,
// rides as the message's trailing line (composedMessage's second parameter)
// — until it resolves the message composes without it, so nothing waits on
// the network to render.
//
// THE SHARE LINK (slice 2): the panel owns ONE GET /me/referral-share-link
// read for the whole section — the Welcome tab's "Your share link" block and
// the composed message both consume it (passing it down; ReferWelcomePanel
// renders the block, it does not refetch). Creating is the Welcome block's
// explicit button (the POST is idempotent, so a double-click cannot churn
// the token).
//
// Blank optionals are OMITTED from the submit body, never sent as "" (the
// binding wire constraint; referral-api.ts enforces it). Success re-seeds
// from the MUTATION RESPONSE (§59 — prepend, no list re-fetch).
//
// Inline styles only — no Tailwind (embed convention). Every <button> is
// explicitly type="button"/type="submit" (project rule §73).
//
// ⚠️ AMENDED 2026-10-09 (account-area redesign, Task 21 BASELINE). The
// submission form's three address inputs carry WHATWG autofill tokens
// (street-address on the combined street field — the §4 wire has ONE street
// value, not line1/line2 — plus address-level2 city, address-level1 state)
// and `name` attributes mirroring their wire keys. Attributes only; values,
// validation and the wire shape are unchanged. "Store name" is a business
// name with no token in the address set; the contact fields are not address
// inputs at all.

import { useState, useEffect, useCallback, useRef, type FormEvent } from "react";
import {
  useEmbedTokens,
  EmbedCard,
  EmbedHeading,
  EmbedText,
  EmbedButton,
  EmbedBanner,
  EmbedField,
  EmbedInput,
  EmbedTextarea,
  EmbedCheckbox,
  EmbedTabs,
  EmbedTable,
  EmbedStatusBadge,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { formatInstantDate } from "../../embed-shared/format";
import {
  listMyReferrals,
  submitReferral,
  createMyReferralShareLink,
  getMyReferralShareLink,
  listRedeemStores,
  getActiveReferralDisclosure,
  referralShareLinkUrl,
  type RedeemStoreWire,
  type ReferralWire,
  type ShareLinkWire,
  type SubmitReferralRequest,
} from "./referral-api";
import {
  ENROLLMENT_ACCEPT_HINT,
  ENROLLMENT_STALE_FALLBACK,
  ENROLLMENT_UNAVAILABLE_MESSAGE,
  EnrollmentAcceptance,
  enrollmentAccepted,
  type EnrollmentGateState,
} from "./enrollment-gate";
import { SECTION_TABS, type SectionId } from "./account-sections";
import { REFER_WELCOME_HEADLINE, ReferWelcomePanel, type ShareLinkBlockState } from "./ReferWelcomePanel";
import { ShareIntentLink, mailtoHref, smsHref } from "./share-intent";
import { RedemptionsPanel } from "./RedemptionsPanel";

// The registry is the single tab vocabulary (the old local SubTabId union —
// "refer" | "redeem" | "payouts" — is retired with the pre-rename naming).
// `refer` is statically present in SECTION_TABS (verified at
// account-sections.ts); the Partial is for the OTHER sections' optional tab
// sets, hence the assertion. The spread satisfies EmbedTabs' items array.
const REFER_TABS = [...SECTION_TABS.refer!];
type ReferTabId = (typeof REFER_TABS)[number]["id"];
const DEFAULT_REFER_TAB: ReferTabId = "welcome";

export interface ReferPanelProps {
  /** A registry tab id ("redemptions" — "View redemptions" on the Overview
   *  landing). Validated against SECTION_TABS.refer: an unknown or retired
   *  id falls back to the Welcome tab. Consumed at mount only (a useState
   *  initializer), so a later render cannot yank the customer off a tab
   *  they switched to themselves. */
  initialSubTab?: string;
  /** Forwarded to RedemptionsPanel's dialog (the "Link a merchant account
   *  in Merchant services" escape hatch routes to that section). */
  onNavigateSection?: (id: SectionId) => void;
  /** TD-605: whether the member holds manage-mids; forwarded to the redeem
   *  dialog (false = redeem by store, never read GET /me/mids). */
  canViewAccounts?: boolean;
}

interface ReferFields {
  store_name: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone: string;
  contact_email: string;
  description: string;
  street: string;
  city: string;
  state: string;
}

const EMPTY_REFER: ReferFields = {
  store_name: "",
  contact_first_name: "",
  contact_last_name: "",
  contact_phone: "",
  contact_email: "",
  description: "",
  street: "",
  city: "",
  state: "",
};

// The composed message template. Blank segments are SKIPPED (an unfilled
// first name greets "Hi there"; no city/state drops the location clause) —
// the message never ships a stray "in ,".
//
// Composition = (a) the referral-form values + (b) the program tagline (the
// Welcome tab's own headline, REFER_WELCOME_HEADLINE — no config-sourced
// tagline exists; see the ReferWelcomePanel constant's comment) + (c) the
// share link, WHEN the caller has one — the single return is the append
// point slice 2 designed for. The link rides as a trailing line so it stays
// copy-paste intact in mail/SMS clients; a message composed before the link
// resolves (or for a caller with none) is identical to the slice-1 shape.
function composedMessage(f: ReferFields, shareUrl?: string | null): string {
  const first = f.contact_first_name.trim();
  const greeting = first ? `Hi ${first},` : "Hi there,";
  const store = f.store_name.trim();
  const location = [f.city.trim(), f.state.trim()].filter(Boolean).join(", ");
  let lead = greeting;
  if (store) lead += ` I suggested ${store}`;
  if (location) lead += ` in ${location}`;
  // ("feel free" is deliberately absent — the copy ban on "free" is applied
  // as a flat string ban, so the template avoids the word entirely.)
  const body = `${lead} to Aldelo as a business that might want to take a look. Aldelo's team may reach out — ask me about my experience with Aldelo any time.`;
  // (b) the program's tagline — the Welcome headline verbatim (one vocabulary
  // with the sales page).
  const base = `${body} ${REFER_WELCOME_HEADLINE}`;
  // (c) the attributed link — omitted entirely while absent, never "".
  return shareUrl ? `${base}\n${shareUrl}` : base;
}

// Share-link wire -> block state. TD-765: `enrollment_disclosure_id === null` is a LEGACY
// UNACCEPTED link (leads refused: prompt to accept); an ABSENT field is an OLD
// server that predates the acceptance triple (the link works: NO prompt) — the
// strict `=== null` is the whole distinction, do not loosen it to a falsy test.
function linkBlockState(link: ShareLinkWire): ShareLinkBlockState {
  return {
    status: "ready",
    url: referralShareLinkUrl(link.token),
    needsAcceptance: link.enrollment_disclosure_id === null,
  };
}

export function ReferPanel({ initialSubTab, onNavigateSection, canViewAccounts }: ReferPanelProps) {
  const t = useEmbedTokens();
  // Mount-time tab seed, validated against the registry (ProfilePanel idiom).
  const [subTab, setSubTab] = useState<ReferTabId>(
    initialSubTab && REFER_TABS.some((tab) => tab.id === initialSubTab)
      ? (initialSubTab as ReferTabId)
      : DEFAULT_REFER_TAB,
  );

  // ---- Eligibility (form AND share link): the Aldelo Pay STORE predicate. ----
  // The server's referral-submit and public-lead gates require a live store
  // owned by the caller with an active, live MID linked to it; the
  // redeem-store list's `has_active_merchant_account` is computed by that same
  // predicate. It is read from /me/referral-redemption-stores (area
  // participate-referrals, the same area as the referral submit and
  // share-link calls), NOT /me/mids (manage-mids), so a refer-only member is
  // not 403'd.
  const [stores, setStores] = useState<RedeemStoreWire[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [storesError, setStoresError] = useState<string | null>(null);

  // ---- Own referrals history (T7 read; every status). ----
  const [referrals, setReferrals] = useState<ReferralWire[]>([]);
  const [loadingReferrals, setLoadingReferrals] = useState(true);
  const [referralsError, setReferralsError] = useState<string | null>(null);

  // ---- The caller's share link (slice 2): ONE read for the whole section —
  // the Welcome tab's "Your share link" block renders it and the composed
  // message appends it. An independent load, like the two above: its failure
  // takes down neither the form nor the history.
  const [shareLink, setShareLink] = useState<ShareLinkBlockState>({ status: "loading", url: null });

  const [fields, setFields] = useState<ReferFields>(EMPTY_REFER);
  const [priorDiscussion, setPriorDiscussion] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // The composed message: derived from the form UNTIL the merchant edits it.
  // The share link rides the prefill as its trailing line once resolved (and
  // a later-created link flows into an UNEDITED message the same way).
  const [message, setMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | null>(null);

  // ---- TD-765: the ACTIVE enrollment disclosure + the member's acceptance of
  // it. ONE read and ONE flag for the whole section: accepting once covers
  // the referral submit, link create, "Get a new link" and legacy activation
  // for the rest of the panel session — until a 409 says the text changed.
  const [enrollment, setEnrollment] = useState<EnrollmentGateState>({
    status: "loading",
    disclosure: null,
    accepted: false,
  });

  const loadEnrollment = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setEnrollment({ status: "loading", disclosure: null, accepted: false });
    try {
      const d = await getActiveReferralDisclosure("enrollment");
      // A new revision (or a first read) always starts UNaccepted.
      setEnrollment((prev) => ({
        status: "ready",
        disclosure: d,
        accepted: prev.disclosure?.id === d.id ? prev.accepted : false,
      }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Refuse, never fall back: no active revision is the program's
        // "not available yet" state, not a failure.
        setEnrollment({ status: "unavailable", disclosure: null, accepted: false });
      } else {
        setEnrollment({
          status: "error",
          disclosure: null,
          accepted: false,
          error: err instanceof ApiError ? err.message : "Failed to load the program terms",
        });
      }
    }
  }, []);

  const setEnrollmentAccepted = useCallback((accepted: boolean) => {
    setEnrollment((prev) => ({ ...prev, accepted }));
  }, []);

  // Maps the TD-765 acceptance errors of a POST to a member-facing message
  // (and updates the gate), or returns null for any other error.
  //   409 DISCLOSURE_STALE         -> reset acceptance, refetch, show new text
  //   503 DISCLOSURE_NOT_CONFIGURED -> not available; nothing is offered
  const handleDisclosureError = useCallback(
    async (err: unknown): Promise<string | null> => {
      if (!(err instanceof ApiError)) return null;
      if (err.code === "DISCLOSURE_STALE") {
        setEnrollment((prev) => ({ ...prev, accepted: false }));
        await loadEnrollment({ silent: true });
        return err.message || ENROLLMENT_STALE_FALLBACK;
      }
      if (err.code === "DISCLOSURE_NOT_CONFIGURED") {
        setEnrollment({ status: "unavailable", disclosure: null, accepted: false });
        return ENROLLMENT_UNAVAILABLE_MESSAGE;
      }
      return null;
    },
    [loadEnrollment],
  );

  const messageOf = useCallback((reason: unknown, fallback: string): string => {
    return reason instanceof ApiError ? reason.message : fallback;
  }, []);

  const loadShareLink = useCallback(async () => {
    setShareLink({ status: "loading", url: null });
    try {
      const link = await getMyReferralShareLink();
      setShareLink(link ? linkBlockState(link) : { status: "none", url: null });
    } catch (err) {
      setShareLink({ status: "error", url: null, error: messageOf(err, "Failed to load your share link") });
    }
  }, [messageOf]);

  // The Welcome block's create button. The POST is idempotent server-side
  // (an existing active link is returned unchanged), so a double-click or a
  // retried click cannot churn the token.
  //
  // TD-765: also the LEGACY ACTIVATION action — rotate:false onto an existing
  // unaccepted link makes the server stamp it in place and keep its token.
  // An acceptance error (409/503) restores the state the member was looking at
  // with the message beside it; any other failure keeps the original error arm.
  const handleCreateShareLink = useCallback(async () => {
    if (!enrollmentAccepted(enrollment) || !enrollment.disclosure) return;
    const before = shareLink;
    setShareLink((prev) => ({ ...prev, status: "loading", error: null }));
    try {
      const link = await createMyReferralShareLink(false, enrollment.disclosure.id);
      setShareLink(linkBlockState(link));
    } catch (err) {
      const disclosureMessage = await handleDisclosureError(err);
      if (disclosureMessage !== null) {
        setShareLink({ ...before, error: disclosureMessage });
        return;
      }
      setShareLink({ status: "error", url: null, error: messageOf(err, "Failed to create your share link") });
    }
  }, [enrollment, shareLink, handleDisclosureError, messageOf]);

  // D6a: "Get a new link". The server's rotate (POST {rotate:true}) revokes the
  // old link and mints a fresh one in ONE transaction, so on success the block
  // simply shows the new URL (and the composed message follows it, being
  // derived from shareLink.url). On failure the OLD link is kept — it is still
  // valid server-side — and the error is shown beside it, not in place of it.
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [rotated, setRotated] = useState(false);
  const handleRotateShareLink = useCallback(async (): Promise<boolean> => {
    if (!enrollmentAccepted(enrollment) || !enrollment.disclosure) return false;
    setRotating(true);
    setRotateError(null);
    setRotated(false);
    try {
      const link = await createMyReferralShareLink(true, enrollment.disclosure.id);
      setShareLink(linkBlockState(link));
      setRotated(true);
      return true;
    } catch (err) {
      // A refused acceptance (409/503) means nothing was written: the old link
      // is untouched, so skip the committed-rotate re-read below.
      const disclosureMessage = await handleDisclosureError(err);
      if (disclosureMessage !== null) {
        setRotateError(disclosureMessage);
        return false;
      }
      // The failure may be a lost RESPONSE to a rotate that DID commit, which
      // kills the old link. Re-read before claiming it still works (M1 review
      // M4): if the server now holds a different link, show that one.
      try {
        const current = await getMyReferralShareLink();
        const currentUrl = current ? referralShareLinkUrl(current.token) : null;
        if (current && currentUrl !== null && currentUrl !== shareLink.url) {
          setShareLink(linkBlockState(current));
          setRotated(true);
          return true;
        }
      } catch {
        // The re-read failed too: fall through to the honest generic error.
      }
      setRotateError(messageOf(err, "Failed to get a new link. Your current link still works."));
      return false;
    } finally {
      setRotating(false);
    }
  }, [enrollment, handleDisclosureError, messageOf, shareLink.url]);

  const loadStores = useCallback(async () => {
    setLoadingStores(true);
    setStoresError(null);
    try {
      const data = await listRedeemStores();
      setStores(Array.isArray(data) ? data : []);
    } catch (err) {
      setStoresError(messageOf(err, "Failed to load your stores"));
    } finally {
      setLoadingStores(false);
    }
  }, [messageOf]);

  const loadReferrals = useCallback(async () => {
    setLoadingReferrals(true);
    setReferralsError(null);
    try {
      const data = await listMyReferrals();
      setReferrals(Array.isArray(data) ? data : []);
    } catch (err) {
      setReferralsError(messageOf(err, "Failed to load your referrals"));
    } finally {
      setLoadingReferrals(false);
    }
  }, [messageOf]);

  useEffect(() => {
    // Independent failures (four independent loads): a store load failure
    // must not take the referrals history — or the share-link block — down
    // with it.
    loadStores();
    loadReferrals();
    loadShareLink();
    loadEnrollment();
  }, [loadStores, loadReferrals, loadShareLink, loadEnrollment]);

  useEffect(() => {
    return () => {
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
    };
  }, []);

  // ONE derived eligibility for the referral FORM and the SHARE LINK (the
  // server's referral-submit and public-lead gates share the predicate): the
  // caller owns an Aldelo Pay store (a live store with an active, live MID
  // linked to it). I-1: the server refuses EVERY public lead (the uniform 404)
  // for a referrer without one, so the form and the share link — in the
  // Welcome block and as the composed message's trailing line — are only
  // worth offering once the store read has SETTLED and at least one store
  // reports has_active_merchant_account. An active MID with no store does NOT
  // qualify. While the read is in flight both are withheld, never flashed. A
  // FAILED read is UNKNOWN, not "no store": both are offered, since the
  // server is the authority (422/404 when ineligible).
  const hasAldeloPayStore = stores.some((s) => s.has_active_merchant_account === true);
  const storesSettled = !loadingStores && !storesError;
  const eligibleOrUnknown = !loadingStores && (!!storesError || hasAldeloPayStore);
  const shareLinkOffered = eligibleOrUnknown;
  // TD-765: a legacy UNACCEPTED link is withheld from the composed message
  // too: its leads are refused, so sharing it would send people to a dead page.
  const shareUrl = shareLinkOffered && !shareLink.needsAcceptance ? shareLink.url : null;
  const shownMessage = message ?? composedMessage(fields, shareUrl);

  const updateField = useCallback((key: keyof ReferFields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setFieldError(null);
    setSubmitSuccess(false);
  }, []);

  const handleCopy = useCallback(() => {
    // Guarded: some embedded contexts expose no clipboard at all — the copy
    // affordance then quietly does nothing (no confirmation is shown).
    const text = message ?? composedMessage(fields, shareUrl);
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
        copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
      },
      () => {
        // A refused clipboard write shows no confirmation.
        setCopied(false);
      },
    );
  }, [message, fields, shareUrl]);

  // S2(i): navigator.share hands the text to the OS share sheet — the
  // customer's own share action, no Aldelo network call. The control only
  // renders where the API exists (feature-detected, never a dead button).
  const handleWebShare = useCallback(() => {
    void navigator.share?.({ text: message ?? composedMessage(fields, shareUrl) });
  }, [message, fields, shareUrl]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      // Arm 2 of the acceptance gate: a DIRECT form submit (Enter key) must
      // not bypass the disabled button.
      if (!enrollmentAccepted(enrollment) || !enrollment.disclosure) return;
      // Every missing required field at once (the server's 422 names them
      // all; the form mirrors that instead of dribbling one per click).
      const required: [keyof ReferFields, string][] = [
        ["store_name", "Store name"],
        ["contact_first_name", "Contact first name"],
        ["contact_last_name", "Contact last name"],
        ["contact_phone", "Contact phone"],
        ["contact_email", "Contact email"],
        ["city", "City"],
        ["state", "State"],
      ];
      const missing = required.filter(([key]) => !fields[key].trim()).map(([, label]) => label);
      if (missing.length > 0) {
        setFieldError(`Please fill in: ${missing.join(", ")}.`);
        return;
      }
      setSubmitting(true);
      setFormError(null);
      setSubmitSuccess(false);
      try {
        // Blank optionals are OMITTED from the body — the key is absent,
        // never `undefined`-valued and never "" (referral-api.ts enforces
        // the wire shape; the test pins the literal object shape).
        const body: SubmitReferralRequest = {
          store_name: fields.store_name,
          contact_first_name: fields.contact_first_name,
          contact_last_name: fields.contact_last_name,
          contact_phone: fields.contact_phone,
          contact_email: fields.contact_email,
          city: fields.city,
          state: fields.state,
          prior_discussion: priorDiscussion,
          enrollment_disclosure_id: enrollment.disclosure.id,
        };
        if (fields.description.trim()) body.description = fields.description;
        if (fields.street.trim()) body.street = fields.street;
        const created = await submitReferral(body);
        // §59: seed the new row from the MUTATION RESPONSE — the server
        // assigned its id/status/version and computed submitted_at/
        // expires_at; a local construction (or a refetch) could only guess.
        setReferrals((prev) => [created, ...prev]);
        setFields(EMPTY_REFER);
        // The whole form clears — prior_discussion is part of the form.
        setPriorDiscussion(false);
        setMessage(null);
        setSubmitSuccess(true);
      } catch (err) {
        const disclosureMessage = await handleDisclosureError(err);
        setFormError(disclosureMessage ?? messageOf(err, "Failed to submit this referral. Please try again."));
      } finally {
        setSubmitting(false);
      }
    },
    [fields, messageOf, priorDiscussion, enrollment, handleDisclosureError],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      {/* Labels live at this call site only — the registry array IS the tab
          set (the amendment: EmbedTabs itself is not modified). */}
      <EmbedTabs
        aria-label="Refer & Save sections"
        value={subTab}
        onChange={(id) => setSubTab(id as ReferTabId)}
        items={REFER_TABS}
      />

      {subTab === "welcome" && (
        <ReferWelcomePanel
          onReferClick={() => setSubTab("refer")}
          shareLink={shareLinkOffered ? shareLink : undefined}
          shareLinkUnavailable={storesSettled && !hasAldeloPayStore}
          onCreateShareLink={handleCreateShareLink}
          onRotateShareLink={handleRotateShareLink}
          rotatingShareLink={rotating}
          rotateShareLinkError={rotateError}
          shareLinkRotated={rotated}
          onRetryShareLink={loadShareLink}
          enrollment={enrollment}
          onEnrollmentAcceptChange={setEnrollmentAccepted}
          onRetryEnrollment={() => void loadEnrollment()}
          onActivateShareLink={handleCreateShareLink}
        />
      )}

      {subTab === "refer" && (
        <>
          {loadingStores ? (
            <p role="status" style={{ margin: 0 }}>
              <EmbedText tone="secondary">Loading your stores...</EmbedText>
            </p>
          ) : storesSettled && !hasAldeloPayStore ? (
            // Known-ineligible only: a SETTLED read with no Aldelo Pay store
            // withholds the form and points at Merchant services. A FAILED
            // read falls through to the form; the server's 422 is the
            // authority.
            <EmbedCard>
              <EmbedText tone="secondary">
                You need a store with an active merchant account to refer a store. Request a
                link under Merchant services; you can refer once Aldelo verifies it.
              </EmbedText>
            </EmbedCard>
          ) : enrollment.status === "unavailable" ? (
            // TD-765: no active enrollment disclosure (404 on the read, or a
            // 503 DISCLOSURE_NOT_CONFIGURED on submit) — nothing to accept, so
            // no submit is offered; never a fallback to other text.
            <EmbedCard>
              <EmbedText tone="secondary">{ENROLLMENT_UNAVAILABLE_MESSAGE}</EmbedText>
            </EmbedCard>
          ) : (
            <EmbedCard>
              <form onSubmit={handleSubmit} noValidate>
                <EmbedHeading level={3}>Refer a store</EmbedHeading>

                {formError && (
                  <div style={{ marginTop: t.spacing.md }}>
                    <EmbedBanner tone="error">{formError}</EmbedBanner>
                  </div>
                )}
                {submitSuccess && !formError && (
                  <div style={{ marginTop: t.spacing.md }}>
                    <EmbedBanner tone="success">
                      Referral submitted. Track its status in the Referrals tab.
                    </EmbedBanner>
                  </div>
                )}
                {fieldError && (
                  <div style={{ marginTop: t.spacing.md }}>
                    <EmbedBanner tone="error">{fieldError}</EmbedBanner>
                  </div>
                )}

                <div style={{ marginTop: t.spacing.md }}>
                  <EmbedField label="Store name" htmlFor="refer-store-name" required>
                    <EmbedInput
                      id="refer-store-name"
                      type="text"
                      value={fields.store_name}
                      onChange={(e) => updateField("store_name", e.target.value)}
                      aria-required="true"
                    />
                  </EmbedField>

                  <EmbedField label="Contact first name" htmlFor="refer-first" required>
                    <EmbedInput
                      id="refer-first"
                      type="text"
                      value={fields.contact_first_name}
                      onChange={(e) => updateField("contact_first_name", e.target.value)}
                      aria-required="true"
                    />
                  </EmbedField>

                  <EmbedField label="Contact last name" htmlFor="refer-last" required>
                    <EmbedInput
                      id="refer-last"
                      type="text"
                      value={fields.contact_last_name}
                      onChange={(e) => updateField("contact_last_name", e.target.value)}
                      aria-required="true"
                    />
                  </EmbedField>

                  <EmbedField label="Contact phone" htmlFor="refer-phone" required>
                    <EmbedInput
                      id="refer-phone"
                      type="tel"
                      value={fields.contact_phone}
                      onChange={(e) => updateField("contact_phone", e.target.value)}
                      aria-required="true"
                    />
                  </EmbedField>

                  <EmbedField label="Contact email" htmlFor="refer-email" required>
                    <EmbedInput
                      id="refer-email"
                      type="email"
                      value={fields.contact_email}
                      onChange={(e) => updateField("contact_email", e.target.value)}
                      aria-required="true"
                    />
                  </EmbedField>

                  <EmbedField label="City" htmlFor="refer-city" required>
                    <EmbedInput
                      id="refer-city"
                      name="city"
                      autoComplete="address-level2"
                      type="text"
                      value={fields.city}
                      onChange={(e) => updateField("city", e.target.value)}
                      aria-required="true"
                    />
                  </EmbedField>

                  <EmbedField label="State" htmlFor="refer-state" required>
                    <EmbedInput
                      id="refer-state"
                      name="state"
                      autoComplete="address-level1"
                      type="text"
                      value={fields.state}
                      onChange={(e) => updateField("state", e.target.value)}
                      aria-required="true"
                    />
                  </EmbedField>

                  <EmbedHeading level={3}>More details (optional)</EmbedHeading>
                  <EmbedField
                    label="Description"
                    htmlFor="refer-description"
                    help="Optional — anything useful for our team reaching out."
                  >
                    <EmbedInput
                      id="refer-description"
                      type="text"
                      value={fields.description}
                      onChange={(e) => updateField("description", e.target.value)}
                    />
                  </EmbedField>

                  <EmbedField label="Street" htmlFor="refer-street" help="Optional.">
                    <EmbedInput
                      id="refer-street"
                      name="street"
                      autoComplete="street-address"
                      type="text"
                      value={fields.street}
                      onChange={(e) => updateField("street", e.target.value)}
                    />
                  </EmbedField>

                  {/* The composed message (Task 20 slice 1): prefilled from
                      the form + the program tagline until edited — a manual
                      edit STOPS the prefill. The four share actions read the
                      SAME state the textarea shows, so an edit always wins. */}
                  <EmbedField
                    label="Message to share"
                    htmlFor="refer-message"
                    help="Editable — this is the message you can share with the store."
                  >
                    <EmbedTextarea
                      id="refer-message"
                      value={shownMessage}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </EmbedField>
                  {/* The share actions (S2(i): Aldelo sends NOTHING —
                      clipboard, mailto:, sms: and the OS share sheet are all
                      pure client-side intents; zero network calls). Share
                      renders only where the Web Share API exists. */}
                  <div
                    style={{
                      marginTop: t.spacing.xs,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: t.spacing.sm,
                    }}
                  >
                    <EmbedButton type="button" variant="secondary" size="sm" onClick={handleCopy}>
                      {copied ? "Copied" : "Copy message"}
                    </EmbedButton>
                    <ShareIntentLink href={mailtoHref(shownMessage)}>Email</ShareIntentLink>
                    <ShareIntentLink href={smsHref(shownMessage)}>Text</ShareIntentLink>
                    {typeof navigator.share === "function" && (
                      <EmbedButton type="button" variant="secondary" size="sm" onClick={handleWebShare}>
                        Share
                      </EmbedButton>
                    )}
                  </div>

                  <div style={{ marginTop: t.spacing.md }}>
                    <EmbedCheckbox
                      id="refer-prior-discussion"
                      checked={priorDiscussion}
                      onChange={(e) => setPriorDiscussion(e.target.checked)}
                      label={
                        <EmbedText tone="secondary" size="xs">
                          Prior discussion — I have already talked with this store about Aldelo.
                        </EmbedText>
                      }
                    />
                  </div>
                </div>

                {/* TD-765: the enrollment terms, shown and explicitly accepted
                    before the referral can be submitted. */}
                <div style={{ marginTop: t.spacing.md }}>
                  {enrollment.status === "loading" && (
                    <p role="status" style={{ margin: 0 }}>
                      <EmbedText tone="secondary">Loading the program terms...</EmbedText>
                    </p>
                  )}
                  {enrollment.status === "error" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm, alignItems: "flex-start" }}>
                      <EmbedBanner tone="error">{enrollment.error ?? "Failed to load the program terms"}</EmbedBanner>
                      <EmbedButton type="button" variant="secondary" size="sm" onClick={() => void loadEnrollment()}>
                        Retry
                      </EmbedButton>
                    </div>
                  )}
                  {enrollment.status === "ready" && enrollment.disclosure && (
                    <>
                      <EmbedHeading level={3}>Program terms</EmbedHeading>
                      <div
                        data-testid="refer-enrollment-body"
                        style={{
                          marginTop: t.spacing.sm,
                          border: `1px solid ${t.colors.borderSubtle}`,
                          borderRadius: t.borderRadius,
                          padding: t.spacing.md,
                          whiteSpace: "pre-wrap",
                          fontSize: t.fontSize.sm,
                          lineHeight: t.lineHeight.normal,
                          color: t.colors.text,
                        }}
                      >
                        {enrollment.disclosure.body}
                      </div>
                      <div style={{ marginTop: t.spacing.md }}>
                        <EnrollmentAcceptance
                          id="refer-enrollment-accept"
                          accepted={enrollment.accepted}
                          onChange={setEnrollmentAccepted}
                        />
                      </div>
                      {!enrollment.accepted && (
                        <p style={{ margin: `${t.spacing.xs}px 0 0` }}>
                          <EmbedText tone="secondary" size="xs">{ENROLLMENT_ACCEPT_HINT}</EmbedText>
                        </p>
                      )}
                    </>
                  )}
                </div>

                <div style={{ marginTop: t.spacing.md }}>
                  <EmbedButton
                    type="submit"
                    variant="primary"
                    disabled={submitting || !enrollmentAccepted(enrollment)}
                    loading={submitting}
                  >
                    {submitting ? "Submitting..." : "Submit referral"}
                  </EmbedButton>
                </div>
              </form>
            </EmbedCard>
          )}
        </>
      )}

      {subTab === "referrals" && (
        <EmbedCard>
          <EmbedHeading level={3}>Referrals</EmbedHeading>
          <div style={{ marginTop: t.spacing.sm }}>
            {referralsError && (
              <div style={{ marginBottom: t.spacing.md }}>
                <EmbedBanner tone="error">{referralsError}</EmbedBanner>
                <div style={{ marginTop: t.spacing.md }}>
                  <EmbedButton type="button" variant="secondary" onClick={loadReferrals}>
                    Retry
                  </EmbedButton>
                </div>
              </div>
            )}
            {loadingReferrals && (
              <p role="status" style={{ margin: 0 }}>
                <EmbedText tone="secondary">Loading your referrals...</EmbedText>
              </p>
            )}
            {!loadingReferrals && !referralsError && referrals.length === 0 && (
              <p role="status" style={{ margin: 0 }}>
                <EmbedText tone="secondary">No referrals yet.</EmbedText>
              </p>
            )}
            {!loadingReferrals && !referralsError && referrals.length > 0 && (
              <EmbedTable
                caption="Your referrals"
                columns={[
                  {
                    id: "store",
                    header: "Store",
                    primary: true,
                    cell: (r: ReferralWire) => <span>{r.store_name}</span>,
                  },
                  {
                    id: "location",
                    header: "Location",
                    cell: (r: ReferralWire) => <span>{`${r.city}, ${r.state}`}</span>,
                  },
                  {
                    id: "submitted",
                    header: "Submitted",
                    cell: (r: ReferralWire) => <span>{formatInstantDate(r.submitted_at)}</span>,
                  },
                  {
                    id: "status",
                    header: "Status",
                    // The one status map (embed-shared/status.ts, domain
                    // "referral") — never a per-panel color map.
                    cell: (r: ReferralWire) => <EmbedStatusBadge domain="referral" value={r.status} />,
                  },
                ]}
                rows={referrals}
                rowKey={(r) => r.id}
                rowTestId={(r) => `referral-row-${r.id}`}
              />
            )}
          </div>
        </EmbedCard>
      )}

      {subTab === "redemptions" && <RedemptionsPanel onNavigateSection={onNavigateSection} canViewAccounts={canViewAccounts} />}
    </div>
  );
}
